import { useEffect, useRef } from 'react'
import { api } from '../api/client'
import { IN_APP } from '../inApp'

// Printing. In a browser, a page's own print (window.print, after setting up
// the printed version). Inside the phone app, which can't print, the same
// page opens in the phone's browser, signed in once by a short-lived link
// (api/auth.py: /handoff), and prints there as soon as it has loaded; its
// print screen also saves a PDF or shares it.

/** The phone app opens links in the phone's browser for us (it says so with window.ASYS_NATIVE_OPEN). */
const phoneOpens = () => IN_APP && typeof window !== 'undefined' && window.ASYS_NATIVE_OPEN && window.ReactNativeWebView

/** Whether this page can print here: anywhere but an older phone app. */
export const canPrint = () => !IN_APP || Boolean(phoneOpens())

/** Print the page with `print` (its own print routine), or, in the phone app, in the phone's browser.
    `extra`: query parameters the page needs to print the same thing there (what was chosen on it). */
export async function printPage(print = () => window.print(), extra = {}) {
  if (!phoneOpens()) return print()
  const params = new URLSearchParams(window.location.search)
  params.delete('in_app')
  Object.entries(extra).forEach(([k, v]) => params.set(k, String(v ?? '')))
  const query = params.toString()
  const { data } = await api.post('/auth/handoff', { path: `${window.location.pathname}${query ? `?${query}` : ''}` })
  window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'asys:open', url: data.url }))
}

/** Opened from the phone app to print (?print=1): print once the page is `ready`. */
export function useAutoPrint(ready, print = () => window.print()) {
  const latest = useRef(print)
  latest.current = print
  const done = useRef(false)
  useEffect(() => {
    if (done.current || !ready) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('print') !== '1') return
    done.current = true
    params.delete('print')
    const query = params.toString()
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
    // A moment for images and fonts.
    setTimeout(() => latest.current(), 600)
  }, [ready])
}
